import { useCallback, useContext, useId, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Pane from 'components/Common/Pane'
import Hr from 'components/Common/Hr'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import IconButton from 'LuxKit/Button/IconButton'
import SolidCalendarAltIcon from 'LuxKit/Icons/solid/SolidCalendarAltIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import useToggle from 'hooks/useToggle'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import ModalContext from 'contexts/ModalContext'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import FlexibleCancellationPolicyTimelineContent from 'components/Common/CancellationPolicies/Flash/FlexibleCancellationPolicyTimelineContent'
import OfferPropertyFlexibleCancellationPolicyTimelineModal from 'components/Common/CancellationPolicies/Flash/OfferPropertyFlexibleCancellationPolicyTimelineModal'
import { CalendarIconWrap } from 'components/Common/CancellationPolicies/Flash/CancellationPolicyTimelineComponents'
import type { FlexibleCancellationTimelineDetails } from 'components/Common/CancellationPolicies/Flash/flexibleCancellationPolicyTimelineUtils'
import { getOfferCancellationCollapsedSummary } from './getOfferCancellationCollapsedSummary'

interface Props {
  details: FlexibleCancellationTimelineDetails
  isCharter: boolean
}

function OfferCancellationPolicyTimeline(props: Props) {
  const { details, isCharter } = props
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)
  const [expanded, toggleExpanded] = useToggle(false)
  const detailsId = useId()
  const fullRefundColour = useRebrandSuccessDarkColour()

  const collapsedCopy = useMemo(() => getOfferCancellationCollapsedSummary({
    details,
    isCharter,
    intl,
    formatDate: intlDateFormatter,
  }), [details, intl, intlDateFormatter, isCharter])

  const heading = intl.formatMessage({
    id: '4xqIeVe41PHKbcrnbbXE',
    defaultMessage: 'Cancellation policy',
    description: 'Heading for the offer page cancellation policy timeline accordion module',
  })

  const nextChangeLabel = intl.formatMessage({
    id: 'rePU_vkR0_HBbH3yMRMl',
    defaultMessage: 'Next change',
    description: 'Label above the next cancellation policy change on the offer page timeline card',
  })

  // Flexible cancellation timelines always include a Date changes section in the expanded/
  // modal content — this collapsed footer label mirrors that policy commitment.
  const dateChangesPermittedLabel = intl.formatMessage({
    id: '0TKL7wbGL0ppC8p_zndD',
    defaultMessage: 'Date changes permitted',
    description: 'Footer label indicating date changes are allowed on offer cancellation timeline card',
  })

  const viewPolicyLabel = intl.formatMessage({
    id: '0jHUdR2MDY71eDCkgPyK',
    defaultMessage: 'View policy',
    description: 'Link text that opens the full cancellation policy modal from the offer timeline card',
  })

  const hideDetailsLabel = intl.formatMessage({
    id: 'D8lA35PUVB1CGI7IuwVb',
    defaultMessage: 'Hide cancellation policy details',
    description: 'Accessible label for collapsing the offer page cancellation policy timeline',
  })

  const showDetailsLabel = intl.formatMessage({
    id: 'BrUHgxLGJAycnjVow2DT',
    defaultMessage: 'Show cancellation policy details',
    description: 'Accessible label for expanding the offer page cancellation policy timeline',
  })

  const onViewPolicy = useCallback(() => {
    showModal(
      <OfferPropertyFlexibleCancellationPolicyTimelineModal
        details={details}
        isCharter={isCharter}
      />,
    )
  }, [details, isCharter, showModal])

  return (
    <Pane
      border="light-foreground"
      borderRadius="S"
      padding={20}
      data-testid="offer-cancellation-policy-timeline"
    >
      <Group direction="vertical" gap={16} fullWidth>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
          <Heading variant="heading6" as="h3">
            {heading}
          </Heading>
          {expanded && (
            <IconButton
              kind="tertiary"
              variant="default"
              size="small"
              shape="circle"
              onClick={toggleExpanded}
              aria-label={hideDetailsLabel}
              aria-expanded={expanded}
              aria-controls={detailsId}
            >
              <LineAngleUpIcon />
            </IconButton>
          )}
        </Group>

        {/* Keep the aria-controls target mounted; only mount timeline content when expanded. */}
        <div id={detailsId}>
          {expanded && (
            <FlexibleCancellationPolicyTimelineContent
              details={details}
              isCharter={isCharter}
            />
          )}
        </div>

        {!expanded && (
          <>
            <Group direction="horizontal" gap={12} verticalAlign="start">
              <CalendarIconWrap>
                <SolidCalendarAltIcon size="XS" />
              </CalendarIconWrap>
              <Group direction="vertical" gap={4}>
                <BodyText
                  variant="medium"
                  weight="bold"
                  colour={collapsedCopy.isStatusHighlighted ? fullRefundColour : undefined}
                >
                  {collapsedCopy.statusTitle}
                </BodyText>
                <BodyText variant="small" colour="light-foreground-alt">
                  {collapsedCopy.statusDescription}
                </BodyText>
              </Group>
            </Group>

            <Hr colour="light-grey" />

            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
              <Group direction="vertical" gap={2}>
                <BodyText variant="small" colour="light-foreground-alt">
                  {nextChangeLabel}
                </BodyText>
                <BodyText variant="medium" weight="bold">
                  {collapsedCopy.nextChangeDetail}
                </BodyText>
              </Group>
              <IconButton
                kind="tertiary"
                variant="default"
                size="small"
                shape="circle"
                onClick={toggleExpanded}
                aria-label={showDetailsLabel}
                aria-expanded={expanded}
                aria-controls={detailsId}
              >
                <LineAngleDownIcon />
              </IconButton>
            </Group>

            <Hr colour="light-grey" />

            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
              <BodyText variant="medium" colour="light-foreground-alt">
                {dateChangesPermittedLabel}
              </BodyText>
              <TextLink size="medium" weight="bold" underline onClick={onViewPolicy}>
                {viewPolicyLabel}
              </TextLink>
            </Group>
          </>
        )}
      </Group>
    </Pane>
  )
}

export default OfferCancellationPolicyTimeline
